import i18n from '@/utils/i18n';
import NavigationIcon from '@assets/icons/navigation.svg';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@/utils/sentry';
import {
  DefaultMainContent,
  ListItem,
  ListItemSeparator,
  ListOption,
} from '@/components/list-item';
import { FONTS, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { GooglePlaces as Google } from '@domain/addresses/blocs/services/google-places';
import { Image, StyleSheet, Text, View } from 'react-native';
import { ListItemType } from '@/components/recycler-list';
import { Selector } from '@/components/selector';
import { useDebouncedCallback } from 'use-debounce';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';

const DEBOUNCE_DELAY_MS = 500;
const LIST_ITEM_HEIGHT = 56;

type PlacesSearchListOption =
  ListOption<google.maps.places.AutocompletePrediction>;

export interface PlacesSearchProps {
  creatable?: boolean;
  initialValue?: string;
  onSelect: (
    name: string,
    placeResult: google.maps.places.PlaceResult | null,
  ) => void;
  onSelectCurrentLocation?: () => void;
  placeholder: string;
  types?: string[];
}

export const PlacesSearchComponent: React.FC<PlacesSearchProps> = ({
  creatable,
  initialValue,
  onSelect,
  onSelectCurrentLocation,
  placeholder,
  types,
}) => {
  const { bottom } = useSafeAreaInsets();

  const initialSearchCalled = useRef(false);
  const [searchPending, setSearchPending] = useState(false);
  const [searchResults, setSearchResults] = useState<PlacesSearchListOption[]>(
    [],
  );
  const { showErrorToast } = useToasts();

  const getSearchResults = useDebouncedCallback(async (input) => {
    if (input) {
      try {
        const predictions = await Google.getPlaceAutocomplete(input, types);
        const results = predictions.map((prediction) => {
          const { description, place_id, structured_formatting } = prediction;
          const { main_text, secondary_text } = structured_formatting || {};
          return {
            metadata: prediction,
            subtitle: secondary_text,
            title: main_text || description,
            value: place_id,
          };
        });

        setSearchResults(results);
      } catch (e) {
        showErrorToast();
        captureException(e);
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }

    setSearchPending(false);
  }, DEBOUNCE_DELAY_MS);

  const sections = useMemo(
    () => [
      {
        data: searchResults,
        key: null,
      },
    ],
    [searchResults],
  );

  useEffect(() => {
    const setInitialOptions = async () => {
      if (!initialSearchCalled.current && initialValue) {
        await getSearchResults(initialValue);
      }
      initialSearchCalled.current = true;
    };
    void setInitialOptions();
  }, [getSearchResults, initialValue]);

  return (
    <Selector<PlacesSearchListOption>
      contentInset={{ bottom: Math.max(bottom, SCREEN_PADDING) }}
      creatable={creatable}
      estimatedItemSize={80}
      initialSearchText={initialValue}
      isLoading={searchPending}
      label={placeholder}
      listHeader={
        <>
          <View style={styles.dataProviderLogoContainer}>
            <Image
              source={require('@assets/images/powered-by-google.png')}
              style={styles.dataProviderLogo}
            />
          </View>
          {onSelectCurrentLocation ? (
            <View style={styles.listItem}>
              <ListItem
                item={{
                  title: i18n.t('current_location'),
                  value: i18n.t('current_location'),
                }}
                onSelect={() => {
                  onSelectCurrentLocation();
                }}
                renderLeftComponent={() => (
                  <NavigationIcon height={22} width={22} />
                )}
              />
              {sections.length && sections[0].data.length ? (
                <ListItemSeparator />
              ) : null}
            </View>
          ) : null}
        </>
      }
      listItemProps={{
        renderMainContent: (option) => {
          if (creatable && option.value === ListItemType.Creatable) {
            return (
              <View style={styles.creatableMainContent}>
                <Text
                  ellipsizeMode="middle"
                  numberOfLines={1}
                  style={[TYPOGRAPHY.medium, styles.creatableTitleBold]}
                  testID="creatable-text"
                >
                  {option.title}
                  <Text style={styles.customText}>{` (${i18n.t(
                    'custom',
                  )})`}</Text>
                </Text>
              </View>
            );
          }

          return <DefaultMainContent item={option} />;
        },
      }}
      onSearchInputChange={(input) => {
        setSearchPending(true);
        getSearchResults(input);
      }}
      onSelect={async (option) => {
        try {
          const prediction = option.metadata;
          onSelect(
            option.title,
            prediction
              ? await Google.getPlaceDetails(prediction.place_id)
              : null,
          );
        } catch (e) {
          showErrorToast();
          captureException(e);
        }
      }}
      placeholder={placeholder}
      sections={sections}
    />
  );
};

const styles = StyleSheet.create({
  creatableMainContent: {
    flex: 1,
  },
  creatableTitleBold: {
    ...FONTS.primarySemiBold,
    paddingVertical: 2,
  },
  customText: {
    ...FONTS.primary,
    // required on android so wrapped text is also bold
    fontWeight: 'normal',
  },
  dataProviderLogo: {
    height: 12,
    width: 100,
  },
  dataProviderLogoContainer: {
    alignItems: 'flex-end',
    marginHorizontal: SCREEN_PADDING,
  },
  listItem: {
    height: LIST_ITEM_HEIGHT,
  },
});
